import {
  AccountType,
  AuthMethod,
  BillingStatus,
  CompanyType,
  Currency,
  DateFormat,
  GeofenceTag,
  Language,
  OrderUnitOfMeasure,
  RateType,
  SaasBillingPlan,
  SiteType,
  StytchMemberStatus,
  SystemOfMeasure,
  TimeFormat,
  TimeZone,
  UnitOfDistance,
  UsageStatus,
  WorkWeekStartDay,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { startCase } from 'lodash-es';

import { ItemNameAndId } from '~types/ItemNameAndId';
import { splitStringBySentenceChange } from '~utils/utilFunctions';

import countriesList from './countries.json';

const data = {
  date_format: {
    values: Object.values(DateFormat),
    default: DateFormat.MM_DD_YYYY,
  },
  language: { values: Object.values(Language), default: Language.EN },
  time_format: { values: Object.values(TimeFormat), default: TimeFormat['12_HOUR'] },
  unit_of_distance: {
    values: Object.values(UnitOfDistance),
    default: UnitOfDistance.MILE,
  },
  system_of_measure: {
    values: Object.values(SystemOfMeasure),
    default: SystemOfMeasure.IMPERIAL,
  },
  currency: { values: Object.values(Currency), default: Currency.USD },
  time_zone: {
    values: Object.values(TimeZone),
    default: TimeZone.US_EASTERN,
  },
  usage_status: {
    values: Object.values(UsageStatus),
    default: UsageStatus.DEMO,
  },
  billing_status: {
    values: Object.values(BillingStatus),
    default: BillingStatus.UNPAID,
  },
  saas_billing_plan: {
    values: Object.values(SaasBillingPlan),
    default: SaasBillingPlan.BASIC,
  },
  stytch_member_status: {
    values: Object.values(StytchMemberStatus),
    default: StytchMemberStatus.PENDING,
  },
  company_type: {
    values: Object.values(CompanyType),
  },
  account_type: {
    values: Object.values(AccountType),
    default: AccountType.CUSTOMER,
  },
  site_type: {
    values: Object.values(SiteType)
      .filter((item) => item !== SiteType.EQUIPMENT_HOME_BASE)
      .map((item) => splitStringBySentenceChange(item)),
    default: null,
  },
  work_week_start_day: {
    values: Object.values(WorkWeekStartDay),
    default: WorkWeekStartDay.MONDAY,
  },
};

// The country a blank address field, an unresolvable geocode, or a company with no
// address falls back to. The overwhelming majority of companies, accounts and sites
// are US-based; the previous Canadian default silently stamped CA on any address
// whose country the FE could not resolve.
const DEFAULT_COUNTRY_CODE = 'US';

const countries = {
  values: countriesList,
  default: countriesList.find((country) => country.code === DEFAULT_COUNTRY_CODE),
};

// System roles are default roles that exist in every company
enum SystemRoles {
  BILLER = 'Biller',
  BILLING_PORTAL = 'Billing Portal',
  COMPANY_ADMIN = 'Company Admin',
  DATA_IMPORTER = 'Data Importer',
  DISPATCHER = 'Dispatcher',
  DRIVER = 'Driver',
  FOREMAN = 'Foreman',
  PLATFORM_ADMIN = 'Platform Admin',
  REPORTING = 'Reporting',
  SALESPERSON = 'salesperson',
  VIEWER = 'Viewer',
}

/**
 * Returns a display-name override for a UOM enum key/value, or undefined when
 * no override applies (caller falls back to startCase(unit.toLowerCase())).
 * Override keys check both the enum key (e.g. "POUND") and the enum value
 * (e.g. "Pound") so callers can pass either.
 */
const getUomDisplayOverride = (unit: string): string | undefined => {
  if (unit === 'POUND' || unit === OrderUnitOfMeasure.POUND) {
    return t('common.uom_pound_short');
  }
  return undefined;
};

const unitKeys = Object.keys(OrderUnitOfMeasure) as (keyof typeof OrderUnitOfMeasure)[];
const unitOfMeasureOptions = unitKeys.map((unit) => {
  const value = OrderUnitOfMeasure[unit];
  return {
    name: getUomDisplayOverride(unit) ?? startCase(unit.toLowerCase()),
    id: value,
  };
});
const GeoFenceTypes = {
  CIRCLE: 'circle',
  POLYGON: 'polygon',
  EQUIPMENT: 'equipment',
};
const geofenceNoneOption = ItemNameAndId.parse({ name: t('common.none'), id: 'none' });
const geoFenceCircleOption = ItemNameAndId.parse({
  name: t('geofence.circle'),
  id: GeoFenceTypes.CIRCLE,
});
const geofenceEquipmentOption = ItemNameAndId.parse({
  name: t('geofence.equipment'),
  id: GeoFenceTypes.EQUIPMENT,
});
const geofencePolygonOption = ItemNameAndId.parse({
  name: t('geofence.polygon'),
  id: GeoFenceTypes.POLYGON,
});
const geofenceOptions = [
  geoFenceCircleOption,
  ItemNameAndId.parse({ name: t('geofence.polygon'), id: GeoFenceTypes.POLYGON }),
  ItemNameAndId.parse({ name: t('geofence.equipment'), id: GeoFenceTypes.EQUIPMENT }),
];

const dateFormatMMDDYYYYOption = ItemNameAndId.parse({
  name: t(
    'administration.configuration.settings.date_format.date_format_options.MMDDYYYY',
  ),
  id: 'MM/DD/YYYY',
});
const dateFormatDDMMYYYYOption = ItemNameAndId.parse({
  name: t(
    'administration.configuration.settings.date_format.date_format_options.DDMMYYYY',
  ),
  id: 'DD/MM/YYYY',
});
const dateFormatYYYYMMDDOption = ItemNameAndId.parse({
  name: t(
    'administration.configuration.settings.date_format.date_format_options.YYYYMMDD',
  ),
  id: 'YYYY/MM/DD',
});

const movingGeoFencesTagsOptions = [
  ItemNameAndId.parse({
    name: t('geofence.tags.loading'),
    id: GeofenceTag.LOADING,
  }),

  ItemNameAndId.parse({
    name: t('geofence.tags.unloading'),
    id: GeofenceTag.UNLOADING,
  }),
  ItemNameAndId.parse({
    name: t('geofence.tags.check_in'),
    id: GeofenceTag.CHECK_IN,
  }),
  ItemNameAndId.parse({
    name: t('geofence.tags.scaling'),
    id: GeofenceTag.SCALING,
  }),
  ItemNameAndId.parse({
    name: t('geofence.tags.ticketing'),
    id: GeofenceTag.TICKETING,
  }),
];
enum ContactTypes {
  SALES = 'sales',
  FOREMAN = 'foreman',
  COLLABORATOR = 'collaborator',
  SUPERVISOR = 'supervisor',
}
const contactTypesOptions = [
  ItemNameAndId.parse({
    name: t('form_fields.contact_salesman'),
    id: ContactTypes.SALES,
  }),
  ItemNameAndId.parse({
    name: t('form_fields.contact_foreman'),
    id: ContactTypes.FOREMAN,
  }),
  ItemNameAndId.parse({
    name: t('form_fields.contact_collaborator'),
    id: ContactTypes.COLLABORATOR,
  }),
  ItemNameAndId.parse({
    name: t('form_fields.contact_supervisor'),
    id: ContactTypes.SUPERVISOR,
  }),
];

// Google Auth & Microsoft Auth are not yet implemented
type AllowedAuthMethod = Exclude<
  AuthMethod,
  AuthMethod.GOOGLE_AUTH | AuthMethod.MICROSOFT_AUTH
>;

enum NoteTypes {
  ORDER = 'order',
  JOB = 'job',
  PROJECT = 'project',
  INTERNAL = 'internal',
}

enum JobAssignmentType {
  VENDOR = 'vendor',
  // Intended to include both internal and external drivers
  DRIVER = 'driver',
}

enum OrderStatusFilter {
  EXTERNAL = 'external',
  ACTIVE = 'active',
  PENDING = 'pending',
  INPROGRESS = 'inProgress',
  DONE = 'done',
  All = 'all',
}

enum ProjectStatusFilter {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  All = 'all',
}

enum AccountTypesFilter {
  CUSTOMER = AccountType.CUSTOMER,
  VENDOR = AccountType.VENDOR,
  ALL = 'all',
}

enum UserTypesFilter {
  USERS = 'users',
  INTERNAL_DRIVERS = 'internal_drivers',
  FOREMEN = 'foremen',
}

enum AddressType {
  PickUp = 'pickUp',
  DropOff = 'dropOff',
  Staging = 'staging',
}

enum SearchParams {
  ACTIVE = 'active',
  DAY_RANGE = 'day_range',
  DONE = 'done',
  DATE_RANGE = 'date_range',
  DISPATCH_NUMBER = 'dispatch_numbers',
  START_DATE = 'start_date',
  END_DATE = 'end_date',
  PROJECT = 'project_ids',
  EXTERNAL_ID = 'external_ids',
  SERVICE = 'service_ids',
  RATE_TYPE = 'rate_types',
  CUSTOMER_ACCOUNT = 'customer_account_ids',
  VENDOR_ACCOUNT = 'vendor_account_ids',
  PICKUP_SITE = 'pickup_site_ids',
  DROPOFF_SITE = 'dropoff_site_ids',
  EQUIPMENT = 'equipment_ids',
  EQUIPMENT_TYPE = 'equipment_type_ids',
  MATERIAL = 'material_ids',
  DRIVER = 'driver_ids',
  STATE = 'states',
  STATUS = 'status',
  TRUCK = 'truck_ids',
  INVOICE_CATEGORY = 'category',
}
enum FetchReason {
  INITIAL_FETCH = 'initial_fetch',
  INFINITE_SCROLL = 'infinite_scroll',
  FILTERS_CHANGED = 'filters_changed',
  REFRESH = 'refresh',
  ORDER_BY_CHANGED = 'order_by_changed',
}

enum SelectedFilter {
  AI_REVIEW_STATUSES = 'aiReviewStatuses',
  COLLABORATORS = 'collaborators',
  CUSTOMERS = 'customers',
  DEPARTMENTS = 'departments',
  DISPATCH_NUMBERS = 'dispatchNumbers',
  DISPATCH_STATUSES = 'dispatchStatuses',
  DRIVERS = 'drivers',
  DUPLICATE_TICKETS = 'duplicateTickets',
  DROP_OFF_SITES = 'dropOffSites',
  EQUIPMENT = 'equipment',
  EQUIPMENT_OWNERS = 'equipmentOwners',
  EQUIPMENT_TYPES = 'equipmentTypes',
  FOREMEN = 'foremen',
  JOB_STATES = 'jobStates',
  MATERIALS = 'materials',
  ORDER_STATES_REFINED = 'refinedOrderStates',
  PICK_UP_SITES = 'pickUpSites',
  PREVAILING_WAGE = 'prevailingWage',
  PROJECTS = 'projects',
  PROJECTS_EXTERNAL_IDS = 'projectsExternalIds',
  REQUESTERS = 'requesters',
  SALESPEOPLE = 'salespeople',
  SERVICE_CLASSES = 'serviceClasses',
  SITE_TYPES = 'siteTypes',
  TICKET_TYPES = 'ticketTypes',
  VENDORS = 'vendors',
}

enum LiveMapSelectedFilter {
  CUSTOMERS = 'customers',
  DEPARTMENTS = 'departments',
  DISPATCH_NUMBERS = 'dispatchNumbers',
  DRIVERS = 'drivers',
  DROP_OFF_SITES = 'dropOffSites',
  PICK_UP_SITES = 'pickUpSites',
  PROJECTS = 'projects',
  PROJECTS_EXTERNAL_IDS = 'projectsExternalIds',
  REQUESTERS = 'requesters',
  SITE_TYPES = 'siteTypes',
  VENDORS = 'vendors',
}

enum SiteSelectedFilter {
  DEPARTMENTS = 'departments',
  SITE_TYPES = 'siteTypes',
  VISIBILITY = 'visibility',
}

export enum EquipmentSelectedFilter {
  SENDER_COMPANIES = 'senderCompanies',
  EQUIPMENT_TYPES = 'equipmentTypes',
  VISIBILITY = 'visibility',
  LABELS = 'labels',
}

export enum DriverSelectedFilter {
  SENDER_COMPANY_TYPES = 'senderCompanyTypes',
  LABELS = 'labels',
}

export enum ResourceUsageLogType {
  DRIVER = 'driver',
  EQUIPMENT = 'equipment',
}

export enum UserSelectedFilter {
  ROLES = 'roles',
  LABELS = 'labels',
}

enum AccountSelectedFilter {
  DEPARTMENTS = 'departments',
  COMPANY_TYPES = 'connected_company.company_types',
  LABELS = 'labels',
}

const rateTypeLabels = Object.entries({
  [RateType.RATE_PER_HOUR]: t('administration.rates.rate_per_hour'),
  [RateType.RATE_PER_LOAD]: t('administration.rates.rate_per_load'),
  [RateType.RATE_PER_TON]: t('administration.rates.rate_per_ton'),
  [RateType.RATE_PER_TONNE]: t('administration.rates.rate_per_tonne'),
  [RateType.RATE_PER_YARD]: t('administration.rates.rate_per_yard'),
  [RateType.RATE_PER_BUSHEL]: t('administration.rates.rate_per_bushel'),
  [RateType.RATE_PER_POUND]: t('administration.rates.rate_per_pound'),
  [RateType.RATE_COMMISSION]: t('administration.rates.rate_commission'),
  [RateType.RATE_FLAT_COMMISSION]: t('administration.rates.rate_flat_commission'),
  [RateType.RATE_FLAT_RATE]: t('administration.rates.rate_flat_rate'),
}).map(([value, name]) => ({ value, name }));

const rateTypeOptions = rateTypeLabels.map((rateType) => ({
  label: rateType.name,
  value: rateType.value,
}));

export {
  AccountSelectedFilter,
  AccountTypesFilter,
  AddressType,
  ContactTypes,
  contactTypesOptions,
  countries,
  data,
  dateFormatDDMMYYYYOption,
  dateFormatMMDDYYYYOption,
  dateFormatYYYYMMDDOption,
  DEFAULT_COUNTRY_CODE,
  FetchReason,
  geoFenceCircleOption,
  geofenceEquipmentOption,
  geofenceNoneOption,
  geofenceOptions,
  geofencePolygonOption,
  GeoFenceTypes,
  getUomDisplayOverride,
  JobAssignmentType,
  LiveMapSelectedFilter,
  movingGeoFencesTagsOptions,
  NoteTypes,
  OrderStatusFilter,
  ProjectStatusFilter,
  rateTypeOptions,
  SearchParams,
  SelectedFilter,
  SiteSelectedFilter,
  SystemRoles,
  unitOfMeasureOptions,
  UserTypesFilter,
};

export type { AllowedAuthMethod };
